import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Spinner } from "@heroui/react";
import { LuArrowLeft, LuPrinter } from "react-icons/lu";
import { useFetchData } from "@/hooks";
import { PrintHeader } from "@/components/ui";
import {
    formatCurrency,
    formatDate,
    formatPrice,
    formatThousands,
} from "@/utils";

const SalesReportPrint = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const params = {
        start_date: searchParams.get("start_date") || "",
        end_date: searchParams.get("end_date") || "",
        customer_id: searchParams.get("customer_id") || undefined,
        material_id: searchParams.get("material_id") || undefined,
        sub_category_id: searchParams.get("sub_category_id") || undefined,
    };

    const { data, isLoading, error } = useFetchData(
        "report/daily-sales",
        params,
    );

    const salesData = data?.sales || [];
    const recapData = data?.recap || [];
    const period = data?.period || {};

    const grandTotal = salesData.reduce(
        (sum, item) => sum + Number(item.subtotal || 0),
        0,
    );
    const totalQuantity = salesData.reduce(
        (sum, item) => sum + Number(item.quantity || 0),
        0,
    );
    const recapGrandTotal = recapData.reduce(
        (sum, item) => sum + Number(item.total_amount || 0),
        0,
    );

    const periodDisplay =
        period.start === period.end
            ? formatDate(period.start)
            : `${formatDate(period.start)} - ${formatDate(period.end)}`;

    if (isLoading) {
        return (
            <div className="flex min-h-screen items-center justify-center">
                <Spinner size="lg" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4">
                <p className="text-default-500">Gagal memuat data.</p>
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate("/sales-report")}
                >
                    Kembali
                </Button>
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-[210mm] text-black">
            {/* Action bar - hidden on print */}
            <div className="sticky top-0 z-10 flex items-center justify-center gap-3 bg-white p-4 print:hidden">
                <Button
                    variant="flat"
                    size="sm"
                    startContent={<LuArrowLeft />}
                    onPress={() => navigate("/sales-report")}
                >
                    Kembali
                </Button>
                <Button
                    color="primary"
                    size="sm"
                    startContent={<LuPrinter />}
                    onPress={() => {
                        const prev = document.title;
                        document.title =
                            period.start &&
                            period.end &&
                            period.start !== period.end
                                ? `Laporan_Penjualan_${period.start}_sd_${period.end}`
                                : `Laporan_Penjualan_${period.start || ""}`;
                        window.print();
                        document.title = prev;
                    }}
                >
                    Print
                </Button>
            </div>

            {/* Print content */}
            <div className="p-8 print:p-0">
                <PrintHeader title="LAPORAN PENJUALAN" />

                <div className="mb-2 text-xs">
                    <span className="font-medium">Periode: </span>
                    {periodDisplay}
                </div>

                {/* Sales Table */}
                {salesData.length > 0 && (
                    <table className="mb-6 w-full border-collapse border border-black text-[11px]">
                        <thead>
                            <tr className="bg-default-100 whitespace-nowrap">
                                <th className="border border-black px-1 py-px text-center">
                                    No.
                                </th>
                                <th className="border border-black px-1 py-px text-left">
                                    Customer
                                </th>
                                <th className="border border-black px-1 py-px text-left">
                                    Barang
                                </th>
                                <th className="border border-black px-1 py-px text-center">
                                    Cara Bayar
                                </th>
                                <th className="border border-black px-1 py-px text-right">
                                    Jumlah
                                </th>
                                <th className="border border-black px-1 py-px text-right">
                                    @
                                </th>
                                <th className="border border-black px-1 py-px text-right">
                                    Total Harga
                                </th>
                                <th className="border border-black px-1 py-px text-center">
                                    Pembayaran
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {salesData.map((item, index) => (
                                <tr key={item.id}>
                                    <td className="border border-black px-1 py-px text-center">
                                        {index + 1}.
                                    </td>
                                    <td className="border border-black px-1 py-px whitespace-nowrap">
                                        {item.customer_name}
                                        <span className="ml-1 text-[9px]">
                                            ({formatDate(item.order_date)})
                                        </span>
                                    </td>
                                    <td className="border border-black px-1 py-px whitespace-nowrap">
                                        {item.material_name}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center">
                                        {item.payment_method_name}
                                    </td>
                                    <td className="border border-black px-1 py-px text-right">
                                        {formatThousands(item.quantity)}{" "}
                                        {item.unit_name}
                                    </td>
                                    <td className="border border-black px-1 py-px text-right">
                                        {formatPrice(item.price)}
                                    </td>
                                    <td className="border border-black px-1 py-px text-right">
                                        {formatCurrency(item.subtotal)}
                                    </td>
                                    <td className="border border-black px-1 py-px text-center whitespace-nowrap">
                                        {item.payment_status_name}
                                        {item.latest_payment_date && (
                                            <span className="ml-1 text-[9px]">
                                                (
                                                {formatDate(
                                                    item.latest_payment_date,
                                                )}
                                                )
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="bg-default-100 font-bold">
                                <td
                                    colSpan={4}
                                    className="border border-black px-1 py-px text-center"
                                >
                                    TOTAL
                                </td>
                                <td className="border border-black px-1 py-px text-right">
                                    {formatThousands(totalQuantity)}
                                </td>
                                <td className="border border-black px-1 py-px"></td>
                                <td className="border border-black px-1 py-px text-right">
                                    {formatCurrency(grandTotal)}
                                </td>
                                <td className="border border-black px-1 py-px"></td>
                            </tr>
                        </tfoot>
                    </table>
                )}

                {/* Recap Table */}
                {recapData.length > 0 && (
                    <>
                        <div className="mb-2 text-sm font-bold">
                            Rekap Penjualan
                        </div>
                        <table className="mb-6 w-full border-collapse border border-black text-[11px]">
                            <thead>
                                <tr className="bg-default-100 whitespace-nowrap">
                                    <th className="border border-black px-1 py-px text-center">
                                        No.
                                    </th>
                                    <th className="border border-black px-1 py-px text-left">
                                        Kategori Barang
                                    </th>
                                    <th className="border border-black px-1 py-px text-right">
                                        Jumlah
                                    </th>
                                    <th className="border border-black px-1 py-px text-right">
                                        Total Harga
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {recapData.map((item, index) => (
                                    <tr key={index}>
                                        <td className="border border-black px-1 py-px text-center">
                                            {index + 1}.
                                        </td>
                                        <td className="border border-black px-1 py-px">
                                            {item.sub_category_name ||
                                                "Tanpa Kategori"}
                                        </td>
                                        <td className="border border-black px-1 py-px text-right">
                                            {formatThousands(
                                                item.total_quantity,
                                            )}{" "}
                                            {item.unit_name}
                                        </td>
                                        <td className="border border-black px-1 py-px text-right">
                                            {formatCurrency(item.total_amount)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="bg-default-100 font-bold">
                                    <td
                                        colSpan={2}
                                        className="border border-black px-1 py-px text-center"
                                    >
                                        TOTAL
                                    </td>
                                    <td className="border border-black px-1 py-px"></td>
                                    <td className="border border-black px-1 py-px text-right">
                                        {formatCurrency(recapGrandTotal)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </>
                )}

                {/* Footer */}
                <div className="text-default-500 mt-8 text-xs">
                    <p>
                        Dicetak pada:{" "}
                        {formatDate(new Date().toISOString(), {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SalesReportPrint;
